import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Container, Title, TitleCount, TitleGroup, TitleWrapper } from '../../components/listing/AMSListing';
import StyledText from '../../components/StyledComponents/StyledText';
import { Form, Formik } from 'formik';
import * as Yup from "yup";
import { Button } from '../../components/form/Button';
import CloseIcon from '../../assets/icons/CloseIcon';
import TwoUsersIcon from '../../assets/icons/TwoUsersIcon';
import MergeConfirmPopup from '../merge/MergeConfirmPopup';
import { useToast } from '../../components/toast/ToastContext';
import { useTheme } from '../../middleware/ThemeContext';
import MergeSingleResult from '../merge/MergeSingleResult';
import MergePreview from '../merge/MergePreview';
import { useGetMultipleOrgRecordsQuery, useMergeOrganizationsMutation } from '../../api/organizationApiSlice';
import PrivateNotesMerge from '../merge/PrivateNotesMerge';
import MergeDuplicateSkeleton from '../merge/MergeDuplicateSkeleton';
import NoData from '../../components/listing/NoData';
import { Data } from '../../types/types';
import { extractUniqueFields, isFieldRequired, isFieldRequiredFromRecords } from '../../utils/helper';
import ArrowIcon from '../../assets/icons/ArrowIcon';

const metaFields = [
    {
        label: 'Organization Name',
        key: 'name'
    },
];

function buildValidationSchema(
  dynamicFields: { 
  crmFieldValues?: { id: number; name: string }[], 
  submissionFields?: { id: number; name: string }[] 
},
  records: any[]
) {
  const schemaShape: Record<string, any> = {
    dataID: Yup.number().required("Primary ID is required"),
    name: isFieldRequired("name",records)
      ? Yup.string().required("Organization Name is required")
      : Yup.string(),
  };

  const allDynamicFields = [
    ...(dynamicFields.crmFieldValues ?? []),
    ...(dynamicFields.submissionFields ?? []),
  ];

  allDynamicFields.forEach((field) => {
    const fieldKey = `${field.name}_${field.id}`;

    schemaShape[fieldKey] = isFieldRequiredFromRecords(records, field.id)
      ? Yup.string().required(`${field.name} is required`)
      : Yup.string();
  });

  return Yup.object().shape(schemaShape);
}

const MergeOrganizations = () => {
  const {theme} = useTheme();
  const noDataRef = useRef<HTMLDivElement>(null);
  const showToast = useToast();
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const idsParam = query.get('ids');

  const idsArray = idsParam
    ? idsParam.split(',').map(id => Number(id.trim()))
    : [];

  const { data: recordData, isLoading, error } = useGetMultipleOrgRecordsQuery(idsParam, {
      skip: !idsParam, refetchOnMountOrArgChange: true
      });
  
  const [mergeOrganizations, { isLoading: loadingMerge, isSuccess, isError, error: err }] = useMergeOrganizationsMutation();
    
  const dynamicFields = extractUniqueFields(recordData);

  const [defaultsApplied, setDefaultsApplied] = useState(false);
    const formikRef = useRef<any>(null);

  const computeCommonValue = (records: any[], extractor: (rec: any) => string): string | null => {
      const values = records.map(extractor).filter(v => !shouldSkipValue(v));
      if(values.length === 0) return null;
      const unique = Array.from(new Set(values));
      return unique.length === 1 ? unique[0] : null;
  };

  const initial_state: Data = {
          metaData: {
              id: 0,
              image: "",
              name: {
                  data_id: 0,
                  value: "",
              },
          },
          formSubmissions: [],
          Ids: [],
          contacts: [],
      };
      const [selectedData, setSelectedData] = useState<Data>(initial_state);

      // Action Functions

    const handleMetaIdUpdate = (newId: number, image: string) => {
        setSelectedData(prev => {
            if (!prev) return prev;

            return {
            ...prev,
            metaData: {
                ...prev.metaData,
                id: newId,
                image: image,
            },
            };
        });
    };
    const updateMetaDataField = (key: string, value: any) => {
        setSelectedData(prev => {
            if (!prev) return prev;

            return {
            ...prev,
            metaData: {
                ...prev.metaData,
                [key]: value,
            },
            };
        });
    };
    const updateIds = (newIds: number[]) => {
        setSelectedData(prev => {
            if (!prev) return prev;

            return {
                ...prev,
                Ids: newIds,
                };
            }
        );
    };
    const updateFields = (data_id: number, id: number, name: string, value: string) => {
        setSelectedData(prev => {
            if (!prev) return prev;

            const submissions = prev.formSubmissions || [];

            const index = submissions.findIndex(item => item.id === id);

            let updatedSubmissions;

            if (index !== -1) {
                updatedSubmissions = [...submissions];
                updatedSubmissions[index] = { data_id, id, name, value };
            } else {
                updatedSubmissions = [...submissions, { data_id, id, name, value }];
            }

            return {
                ...prev,
                formSubmissions: updatedSubmissions
            };
        });
    };

    const updateContactIds = (data_id: number, contactsCount: number) => {
        setSelectedData(prev => {
            if (!prev) return prev;

            const submissions = prev.contacts || [];

            const index = submissions.findIndex(item => item.data_id === data_id);

            let updatedContactIds;

            if (index !== -1) {
                updatedContactIds = submissions.filter(item => item.data_id !== data_id);
            } else {
                updatedContactIds = [...submissions, { data_id, contactsCount }];
            }

            return {
                ...prev,
                contacts: updatedContactIds
            };
        });
    };

    useEffect(() => {
        const duplicateIDS = idsArray?.filter(id => id !== selectedData.metaData.id) || [];
        updateIds(duplicateIDS);

    },[selectedData.metaData.id]);

    const crmFieldsValues = dynamicFields.crmFieldValues.reduce((acc, field) => {
        acc[`${field.name+'_'+field.id}`] = '';
        return acc;
    }, {} as Record<string, any>);

    const submissionFields = dynamicFields.submissionFields.reduce((acc, field) => {
        acc[`${field.name+'_'+field.id}`] = '';
        return acc;
    }, {} as Record<string, any>);

      const initialValues = {
        name: '',
        dataID: null,
        ...crmFieldsValues,
        ...submissionFields,
    }

    const validationSchema = buildValidationSchema(dynamicFields, recordData);

      // auto-default effect using formikRef moved outside of Formik render
        useEffect(() => {
            if (!defaultsApplied && recordData && recordData.length > 0 && formikRef.current) {
                const primary = recordData[0];

                // Helper: Get all non-empty values for a field with their source contact ID
                const getAllValidValues = (extractor: (c: any) => any): { value: any; data_id: number }[] => {
                    return recordData
                        .map((c: any) => {
                            const val = extractor(c);
                            return val && !shouldSkipValue(val) ? { value: val, data_id: c.id } : null;
                        })
                        .filter((item: { value: any; data_id: number } | null): item is { value: any; data_id: number } => item !== null);
                };

                // Helper: Return value ONLY if ALL non-empty values are identical (consensus)
                const getConsensusValue = (extractor: (c: any) => any): { value: any; data_id: number } | null => {
                    const validValues = getAllValidValues(extractor);
                    if (validValues.length === 0) return null;
                    
                    const uniqueValues = Array.from(new Set(validValues.map((v: { value: any; data_id: number }) => v.value)));
                    // ✅ Return only if there's exactly ONE unique value across ALL records
                    if (uniqueValues.length === 1) {
                        return validValues[0];
                    }
                    // ❌ Multiple different values → no consensus → return null
                    return null;
                };

                handleMetaIdUpdate(primary.id, primary.image);
                formikRef.current.setFieldValue('dataID', primary.id);

                // Organization Name field with consensus logic
                const nameExtractor = (r: any) => getFieldValue(r, 'name');
                
                // 1️⃣ Check for consensus across ALL records
                const nameConsensus = getConsensusValue(nameExtractor);
                if (nameConsensus && !shouldSkipValue(nameConsensus.value)) {
                    updateMetaDataField('name', { data_id: nameConsensus.data_id, value: nameConsensus.value });
                    formikRef.current.setFieldValue('name', nameConsensus.value);
                } else {
                    // 2️⃣ No consensus → Check if primary is empty
                    const primaryName = getFieldValue(primary, 'name');
                    if (shouldSkipValue(primaryName)) {
                        // Primary empty → look for consensus among OTHER records only
                        const otherRecords = recordData.filter((c: any) => c.id !== primary.id);
                        const otherValidValues = otherRecords
                            .map((c: any) => {
                                const val = nameExtractor(c);
                                return val && !shouldSkipValue(val) ? { value: val, data_id: c.id } : null;
                            })
                            .filter((item: { value: any; data_id: number } | null): item is { value: any; data_id: number } => item !== null);
                        
                        if (otherValidValues.length > 0) {
                            const uniqueOtherValues = Array.from(new Set(otherValidValues.map((v: { value: any; data_id: number }) => v.value)));
                            // ✅ Only use fallback if OTHER records agree on ONE value
                            if (uniqueOtherValues.length === 1) {
                                updateMetaDataField('name', { data_id: otherValidValues[0].data_id, value: otherValidValues[0].value });
                                formikRef.current.setFieldValue('name', otherValidValues[0].value);
                            }
                        }
                        // ❌ Other records disagree or all empty → leave empty
                    }
                    // 3️⃣ Primary has value BUT no consensus → ❌ SKIP (conflict detected)
                    // Do nothing → leave field empty for user to decide manually
                }

                // Dynamic submission fields with consensus logic
                dynamicFields?.submissionFields?.forEach((field: { name: string; id: number }) => {
                    const extractor = (r: any) => {
                        const m = r?.cfr?.formSubmissions?.submissionValues?.find((s: any) => s.field?.id === field.id);
                        return m?.value ?? '-';
                    };
                    const fieldKey = `${field.name}_${field.id}`;

                    // 1️⃣ Consensus across ALL
                    const consensus = getConsensusValue(extractor);
                    if (consensus && !shouldSkipValue(consensus.value)) {
                        updateFields(consensus.data_id, field.id, field.name, consensus.value);
                        formikRef.current.setFieldValue(fieldKey, consensus.value);
                        return;
                    }

                    // 2️⃣ Primary empty → check others for consensus
                    const primaryMatched = primary?.cfr?.formSubmissions?.submissionValues?.find(
                        (s: any) => s.field?.id === field.id
                    );
                    const primaryValue = primaryMatched?.value ?? '-';
                    
                    if (shouldSkipValue(primaryValue)) {
                        const otherRecords = recordData.filter((c: any) => c.id !== primary.id);
                        const otherValidValues = otherRecords
                            .map((c: any) => {
                                const m = c?.cfr?.formSubmissions?.submissionValues?.find((s: any) => s.field?.id === field.id);
                                const val = m?.value ?? '-';
                                return val && !shouldSkipValue(val) ? { value: val, data_id: c.id } : null;
                            })
                            .filter((item: { value: any; data_id: number } | null): item is { value: any; data_id: number } => item !== null);
                        
                        if (otherValidValues.length > 0) {
                            const uniqueValues = Array.from(new Set(otherValidValues.map((v: { value: any; data_id: number }) => v.value)));
                            if (uniqueValues.length === 1) {
                                updateFields(otherValidValues[0].data_id, field.id, field.name, otherValidValues[0].value);
                                formikRef.current.setFieldValue(fieldKey, otherValidValues[0].value);
                                return;
                            }
                        }
                    }
                    // ❌ No consensus OR primary conflicts → skip
                });

                // Dynamic CRM fields with consensus logic
                dynamicFields?.crmFieldValues?.forEach((field: { name: string; id: number }) => {
                    const extractor = (r: any) => {
                        const m = r?.cfr?.crmFieldValues?.find((s: any) => s.field?.id === field.id);
                        return m?.value ?? '-';
                    };
                    const fieldKey = `${field.name}_${field.id}`;

                    // 1️⃣ Consensus across ALL
                    const consensus = getConsensusValue(extractor);
                    if (consensus && !shouldSkipValue(consensus.value)) {
                        updateFields(consensus.data_id, field.id, field.name, consensus.value);
                        formikRef.current.setFieldValue(fieldKey, consensus.value);
                        return;
                    }

                    // 2️⃣ Primary empty → check others for consensus
                    const primaryMatched = primary?.cfr?.crmFieldValues?.find(
                        (s: any) => s.field?.id === field.id
                    );
                    const primaryValue = primaryMatched?.value ?? '-';
                    
                    if (shouldSkipValue(primaryValue)) {
                        const otherRecords = recordData.filter((c: any) => c.id !== primary.id);
                        const otherValidValues = otherRecords
                            .map((c: any) => {
                                const m = c?.cfr?.crmFieldValues?.find((s: any) => s.field?.id === field.id);
                                const val = m?.value ?? '-';
                                return val && !shouldSkipValue(val) ? { value: val, data_id: c.id } : null;
                            })
                            .filter((item: { value: any; data_id: number } | null): item is { value: any; data_id: number } => item !== null);
                        
                        if (otherValidValues.length > 0) {
                            const uniqueValues = Array.from(new Set(otherValidValues.map((v: { value: any; data_id: number }) => v.value)));
                            if (uniqueValues.length === 1) {
                                updateFields(otherValidValues[0].data_id, field.id, field.name, otherValidValues[0].value);
                                formikRef.current.setFieldValue(fieldKey, otherValidValues[0].value);
                                return;
                            }
                        }
                    }
                    // ❌ No consensus OR primary conflicts → skip
                });

                setDefaultsApplied(true);
                setTimeout(() => formikRef.current?.validateForm(), 0);
            }
        }, [recordData, defaultsApplied, dynamicFields]);

    const ConfirmMerge = async () => {
      const allFields = selectedData.formSubmissions.map(({id, value}) => ({
          fieldId: id,
          value,
      }));

      const contactIDS = selectedData.contacts?.map(contact => contact.data_id);

        const mergePayload = {
            "id": selectedData.metaData.id,
            "duplicateOrgId": selectedData.Ids,
            "mergeContacts": contactIDS,
            "organizationMeta": {
                "name": selectedData.metaData.name?.value,
                "image": selectedData.metaData.image
            },
            "fields": allFields,
        }
        
        try {
        const result = await mergeOrganizations(mergePayload).unwrap();
        showToast('Success', 'Merged organizations successfully', 'success');
        setShowPopup(false);
        navigate(`/home/organizations/${selectedData.metaData.id}`, { state: { from: '/merge', scope: 'organization' } });
        } catch (err) {
        console.error('Merge failed:', err);
        showToast('Error', 'Failed to merge organizations', 'error');
        }
    }

      const handleSubmit = () => {
        if(validationSchema){
            setShowPopup(true);
            
        } else {
            showToast('Error', 'Please select all fields', 'error');
        }
    };
    const onCancel = () => {
        setShowPopup(false);
    }
    const onSuccess = () => {
        ConfirmMerge();
    }

    if (isLoading) return <MergeDuplicateSkeleton />;
    
    if (error) return(
      <div style={{ position: 'relative' }}>
      <NoData
          ref={noDataRef}
          title={"No Organizations Found"}
          subtitle={"Something went wrong no data found against the provided organization ID's"}
        />
        </div>
    );

    // Helper: Get field value (matches logic in MergeSingleResult)
    const getFieldValue = (item: any, key: string): string => {
        const value = item?.[key];
        if (typeof value === 'object' && value !== null) return '-';
        return value !== undefined && value !== null ? value : '-';
    };

    // Helper: Check if value should be skipped
    const shouldSkipValue = (value: any): boolean => {
        return value === null || value === undefined || value === '' || value === '-';
    };

    // NEW: Select ALL fields from an organization AND sync with Formik (skip empty values)
    const handleSelectAllFieldsFromOrg = (org: any, formik: any) => {
        // 1. Update primary ID and image (always update these)
        handleMetaIdUpdate(org.id, org.image);
        formik.setFieldValue('dataID', org.id);
        
        // 2. Update organization name (skip if empty)
        const nameValue = getFieldValue(org, 'name');
        
        // ✅ SKIP if empty or '-'
        if (!shouldSkipValue(nameValue)) {
            updateMetaDataField('name', {
                data_id: org.id,
                value: nameValue,
            });
            
            // ✅ ALSO update Formik
            formik.setFieldValue('name', nameValue);
        }
        
        // 3. Update dynamic submission fields (skip if empty)
        dynamicFields?.submissionFields?.forEach(field => {
            const matchedField = org?.cfr?.formSubmissions?.submissionValues?.find(
                (submission: any) => submission.field?.id === field.id
            );
            const value = matchedField?.value ?? '-';
            
            // ✅ SKIP if empty or '-'
            if (shouldSkipValue(value)) return;
            
            const fieldKey = `${field.name}_${field.id}`;
            
            // Update your state
            updateFields(org.id, field.id, field.name, value);
            
            // ✅ ALSO update Formik
            formik.setFieldValue(fieldKey, value);
        });
        
        // 4. Update dynamic CRM fields (skip if empty)
        dynamicFields?.crmFieldValues?.forEach(field => {
            const matchedField = org?.cfr?.crmFieldValues?.find(
                (submission: any) => submission.field?.id === field.id
            );
            const value = matchedField?.value ?? '-';
            
            // ✅ SKIP if empty or '-'
            if (shouldSkipValue(value)) return;
            
            const fieldKey = `${field.name}_${field.id}`;
            
            // Update your state
            updateFields(org.id, field.id, field.name, value);
            
            // ✅ ALSO update Formik
            formik.setFieldValue(fieldKey, value);
        });

        // 🆕 5. AUTO-FILL: For empty organization name, look for data from other organizations
        const primaryNameValue = getFieldValue(org, 'name');
        if (shouldSkipValue(primaryNameValue)) {
            const otherOrgs = recordData?.filter((o: any) => o.id !== org.id) || [];
            
            for (const otherOrg of otherOrgs) {
                const otherNameValue = getFieldValue(otherOrg, 'name');
                
                if (!shouldSkipValue(otherNameValue)) {
                    // Found name data in another organization, auto-select it
                    updateMetaDataField('name', {
                        data_id: otherOrg.id,
                        value: otherNameValue,
                    });
                    
                    // Sync Formik
                    formik.setFieldValue('name', otherNameValue);
                    break; // Take the first non-empty value found
                }
            }
        }

        // 🆕 6. AUTO-FILL: For empty dynamic submission fields, look for data from other organizations
        dynamicFields?.submissionFields?.forEach(field => {
            const matchedField = org?.cfr?.formSubmissions?.submissionValues?.find(
                (submission: any) => submission.field?.id === field.id
            );
            const primaryValue = matchedField?.value ?? '-';
            
            // If primary organization field is empty, look for data in other organizations
            if (shouldSkipValue(primaryValue)) {
                const otherOrgs = recordData?.filter((o: any) => o.id !== org.id) || [];
                
                for (const otherOrg of otherOrgs) {
                    const otherMatchedField = otherOrg?.cfr?.formSubmissions?.submissionValues?.find(
                        (submission: any) => submission.field?.id === field.id
                    );
                    const otherValue = otherMatchedField?.value ?? '-';
                    
                    if (!shouldSkipValue(otherValue)) {
                        // Found data in another organization, auto-select it
                        updateFields(otherOrg.id, field.id, field.name, otherValue);
                        
                        const fieldKey = `${field.name}_${field.id}`;
                        formik.setFieldValue(fieldKey, otherValue);
                        break; // Take the first non-empty value found
                    }
                }
            }
        });

        // 🆕 7. AUTO-FILL: For empty dynamic CRM fields, look for data from other organizations
        dynamicFields?.crmFieldValues?.forEach(field => {
            const matchedField = org?.cfr?.crmFieldValues?.find(
                (submission: any) => submission.field?.id === field.id
            );
            const primaryValue = matchedField?.value ?? '-';
            
            // If primary organization field is empty, look for data in other organizations
            if (shouldSkipValue(primaryValue)) {
                const otherOrgs = recordData?.filter((o: any) => o.id !== org.id) || [];
                
                for (const otherOrg of otherOrgs) {
                    const otherMatchedField = otherOrg?.cfr?.crmFieldValues?.find(
                        (submission: any) => submission.field?.id === field.id
                    );
                    const otherValue = otherMatchedField?.value ?? '-';
                    
                    if (!shouldSkipValue(otherValue)) {
                        // Found data in another organization, auto-select it
                        updateFields(otherOrg.id, field.id, field.name, otherValue);
                        
                        const fieldKey = `${field.name}_${field.id}`;
                        formik.setFieldValue(fieldKey, otherValue);
                        break; // Take the first non-empty value found
                    }
                }
            }
        });
        
        // 🎯 Force Formik to re-validate after bulk update
        setTimeout(() => {
            formik.validateForm();
        }, 0);
    };

  return (
    <>
    <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
            innerRef={formikRef}
      >
          {(formik) => {
              const { errors, submitCount } = formik;

              // no inline effect; defaults handled via component-level hook

              return (
          <Form>
      <Container theme={theme} className='rounded-0'>
          <div className="d-flex justify-content-between mt-0">
                <div className="d-flex align-items-center gap-2">
                    <TitleGroup className='mb-1'>
                        <TitleWrapper className='align-items-center'>
                            <ArrowIcon className="cursor-pointer" onClick={() => navigate(-1)} color={theme.CorporateTheme.colors.textSecondary} />
                            <Title theme={theme}>Merge Duplicate Organizations</Title>
                            <TitleCount theme={theme}>({recordData ? recordData.length : 0})</TitleCount>
                        </TitleWrapper>
                        <StyledText fontSize="14px" fontWeight={300} textColor={theme.CorporateTheme.colors.textSecondary}>
                            All duplicate organizations will show here
                        </StyledText>
                    </TitleGroup>
                </div>
                <Button theme={theme} type="submit" $variant="add">
                        <TwoUsersIcon color={theme.CorporateTheme.colors.background} />
                        <label className='cursor-pointer'>
                            Merge Duplicates
                        </label>
                </Button>
          </div>
      </Container>
      
          <div className="container-fluid" style={{ backgroundColor: theme.CorporateTheme.colors.bannerBg, height: 'calc(100vh - 205px)' }}>
                  <div className="row pt-4 px-2">
                  <div className="col-9">
                      <div className='d-flex gap-4 justify-content-between'>
                          <StyledText>Duplicate Results</StyledText>
                          {submitCount > 0 && Object.keys(errors).length > 0 && 
                              <StyledText
                                  // color='red'
                                  textColor='red'
                              >
                                  Please select all fields.
                              </StyledText>
                          }
                          
                      </div>
                      <WhiteContainer theme={theme} style={{ height: 'calc(100vh - 290px)', overflow: 'auto', border: `${(submitCount > 0 && Object.keys(errors).length > 0) ? '1px solid red' : ''}` }}>
                          
                          <MergeSingleResult 
                              scope='ORGANIZATION'
                              data={recordData} 
                              columns={metaFields} 
                              dynamicFields={dynamicFields} 
                              initialState={selectedData}
                              handelChange={formik.handleChange}
                              onClick={(field_key, value) => formik.setFieldValue(field_key, value)}
                              handlePrimaryID={handleMetaIdUpdate}
                              handleMeta={updateMetaDataField}
                              handleFormFields={updateFields}
                              onSelectAllFields={(org) => handleSelectAllFieldsFromOrg(org, formik)}
                          />
                          <PrivateNotesMerge
                              data={recordData}
                              scope='ORGANIZATION'
                              initialState={selectedData}
                              updataNotes={updateContactIds}
                          />
                      </WhiteContainer>
                  </div>
                  <div className="col-3">
                      <StyledText>Merge Preview</StyledText>
                              <MergePreview scope='ORGANIZATION' data={selectedData} columns={metaFields} dynamicFields={dynamicFields} />
                  </div>
                  </div>
          </div>
          </Form>
          );
          }}
      </Formik>
      {showPopup &&
          <MergeConfirmPopup 
              title='Confirm Merge?'
              subTitle='Are you sure you want to merge these organizations?  Merging organizations is an irreversible process. Once merged, cannot be undo'
              onCancel={() => onCancel()}
              onConfirm={() => onSuccess()}
              loading={loadingMerge}
          />
      }
    </>
  )
}

const WhiteContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.colors.contentBackground,
  borderRadius: '5px',
  padding: '0px 0px 12px 2px',
  marginTop: '20px',
}));

export default MergeOrganizations